import { roundMoney, roundSkuPrices } from "@shared/lib/money";
import type { SalesforceProduct } from "@shared/services";
import type { CountryApprovalValues, ProductTargetMetadata, RoasterApprovalRequest } from "@shared/services/salesforce/types";

/** Rounds a `number | string` money field to 2 decimal places, preserving its
 *  original type (Salesforce fields like `MSRP__c` can arrive as either).
 *  A string is only rounded when it's numeric in its entirety — `Number()`
 *  (unlike `Number.parseFloat`) rejects trailing non-numeric characters
 *  (e.g. "16.615USD"), so a malformed value is left unchanged rather than
 *  silently truncated to its numeric prefix. */
const roundNumericMoneyField = (value: number | string): number | string => {
  if (typeof value === "number") return roundMoney(value);
  const trimmed = value.trim();
  if (trimmed === "") return value;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? roundMoney(numeric).toFixed(2) : value;
};

/** Rounds the `skuPrices` embedded in a `Target_Metadata__c` JSON string.
 *  `Target_Metadata__c` is produced entirely by our own serialization code, so
 *  a parse failure here indicates a real bug rather than a boundary condition
 *  worth swallowing. */
const roundTargetMetadata = (value: string | null | undefined): string | null | undefined => {
  if (!value) return value;
  const metadata = JSON.parse(value) as ProductTargetMetadata;
  if (!metadata.skuPrices) return value;
  return JSON.stringify({ ...metadata, skuPrices: roundSkuPrices(metadata.skuPrices) });
};

/** Rounds every MSRP/wholesale/web-price money field on a Salesforce product
 *  payload to 2 decimal places before it goes out over the wire, regardless of
 *  how many decimals the roaster typed upstream. Returns a new object. */
export const roundSalesforceProduct = (product: Partial<SalesforceProduct>): Partial<SalesforceProduct> => ({
  ...product,
  ...(product.MSRP__c !== undefined ? { MSRP__c: roundNumericMoneyField(product.MSRP__c) } : {}),
  ...(product.Wholesale_price__c !== undefined ? { Wholesale_price__c: roundNumericMoneyField(product.Wholesale_price__c) } : {}),
  ...(product.Web_price__c !== undefined ? { Web_price__c: roundMoney(product.Web_price__c) } : {}),
  Target_Metadata__c: roundTargetMetadata(product.Target_Metadata__c),
});

/** Rounds every MSRP/wholesale/web-price money field on a ship-to country
 *  entry to 2 decimal places before it goes out over the wire. Returns a new
 *  object. */
const roundApprovalCountry = (country: CountryApprovalValues): CountryApprovalValues => ({
  ...country,
  MSRP: country.MSRP !== undefined ? roundMoney(country.MSRP) : country.MSRP,
  currentMSRP: country.currentMSRP !== undefined ? roundMoney(country.currentMSRP) : country.currentMSRP,
  wholesalePrice: country.wholesalePrice !== undefined ? roundMoney(country.wholesalePrice) : country.wholesalePrice,
  currentWholesalePrice:
    country.currentWholesalePrice !== undefined ? roundMoney(country.currentWholesalePrice) : country.currentWholesalePrice,
  webPrice: country.webPrice !== undefined ? roundMoney(country.webPrice) : country.webPrice,
  currentWebPrice: country.currentWebPrice !== undefined ? roundMoney(country.currentWebPrice) : country.currentWebPrice,
  skuPrices: roundSkuPrices(country.skuPrices),
});

/** Rounds every money field on an approval request's ship-to countries to 2
 *  decimal places before it goes out over the wire. Returns a new object. */
export const roundApprovalRequest = (request: RoasterApprovalRequest): RoasterApprovalRequest => ({
  ...request,
  shipToCountries: request.shipToCountries?.map(roundApprovalCountry),
});
